/**
 * Scope and filter controls, shared in FORM but never in STATE: each mode passes
 * its own values and setters, so a Queue machine selection can never leak into
 * History's request (and vice versa).
 *
 * Note what is deliberately absent from the Queue's controls: there is no date,
 * status, ordering or confidence input anywhere, because Queue membership is
 * "still needs a decision" and nothing in this UI may hide that work.
 */
import { useTranslation } from "react-i18next";
import { Check, ChevronDown } from "lucide-react";
import { Slider } from "@/ui/components/ui/slider";
import type { AlertOrdering } from "@/alerts/types/machine-alert";
import { cn } from "@/ui/lib/utils";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/ui/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/components/ui/select";

const TRIGGER_CLASSES =
  "flex h-8 items-center gap-1.5 rounded-md border border-border bg-background px-2.5 text-xs transition-colors hover:bg-muted";

export interface Option<T extends string | number> {
  value: T;
  label: string;
}

/** Single-choice scope selector (location). */
export const ScopeSelect = <T extends string | number>({
  label,
  value,
  options,
  placeholder,
  onChange,
  disabled,
}: {
  label: string;
  value: T | null;
  options: Option<T>[];
  placeholder: string;
  onChange: (value: T | null) => void;
  disabled?: boolean;
}) => (
  <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
    <span className="sr-only sm:not-sr-only">{label}</span>
    <Select
      value={value == null ? "" : String(value)}
      disabled={disabled}
      onValueChange={(next) => {
        const match = options.find((option) => String(option.value) === next);
        onChange(match ? match.value : null);
      }}
    >
      <SelectTrigger className="h-8 w-[190px] text-xs">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={String(option.value)} className="text-xs">
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </label>
);

/** Multi-choice filter rendered as a checkbox menu. */
export const MultiSelect = <T extends string | number>({
  label,
  values,
  options,
  onChange,
  disabled,
}: {
  label: string;
  values: T[];
  options: Option<T>[];
  onChange: (values: T[]) => void;
  disabled?: boolean;
}) => {
  const { t } = useTranslation("alerts");
  // `label` is already translated by the caller; only the surrounding wording is
  // resolved here, in one key each, so word order can differ per language.
  const scope = label.toLowerCase();
  const summary =
    values.length === 0
      ? t("filters.multi.all", { label: scope })
      : values.length === 1
        ? options.find((option) => option.value === values[0])?.label ??
          t("filters.multi.selected", { n: 1, label: scope })
        : t("filters.multi.selected", { n: values.length, label: scope });

  const toggle = (value: T) =>
    onChange(values.includes(value) ? values.filter((v) => v !== value) : [...values, value]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <button
          type="button"
          className={cn(TRIGGER_CLASSES, disabled && "cursor-not-allowed opacity-50")}
        >
          <span className="max-w-[160px] truncate">{summary}</span>
          <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
        {options.map((option) => (
          <DropdownMenuCheckboxItem
            key={option.value}
            checked={values.includes(option.value)}
            onSelect={(event) => event.preventDefault()}
            onCheckedChange={() => toggle(option.value)}
            className="text-xs"
          >
            {option.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/** Rows-per-page. Changing it resets pagination on both sides — see the store. */
export const PageSizeSelect = ({
  value,
  options,
  onChange,
}: {
  value: number;
  options: readonly number[];
  onChange: (value: number) => void;
}) => {
  const { t } = useTranslation("alerts");
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className={TRIGGER_CLASSES}>
          {t("filters.pageSize.trigger", { size: value })}
          <ChevronDown className="h-3 w-3 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {options.map((option) => (
          <DropdownMenuCheckboxItem
            key={option}
            checked={option === value}
            onCheckedChange={() => onChange(option)}
            className="text-xs"
          >
            {t("filters.pageSize.option", { size: option })}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const ActiveFilterHint = ({ count }: { count: number }) => {
  const { t } = useTranslation("alerts");
  return count === 0 ? null : (
    <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
      <Check className="h-3 w-3" />
      {t("filters.activeCount", { count })}
    </span>
  );
};

/**
 * Min–max confidence on the matching classification row.
 *
 * Unset means "don't filter by confidence at all", which is NOT the same as
 * `0..1`: sending the full range would still exclude every alert whose active
 * row carries no confidence. So the control ships a clear affordance and only
 * emits bounds once the operator has actually moved it.
 */
export const ConfidenceRange = ({
  min,
  max,
  onChange,
}: {
  min: number | null;
  max: number | null;
  onChange: (min: number | null, max: number | null) => void;
}) => {
  const { t } = useTranslation("alerts");
  const active = min != null || max != null;
  const value = [min ?? 0, max ?? 1];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className={cn(TRIGGER_CLASSES, active && "border-primary text-primary")}>
          {active
            ? t("filters.confidence.range", {
                min: Math.round(value[0] * 100),
                max: Math.round(value[1] * 100),
              })
            : t("filters.confidence.any")}
          <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64 p-3">
        <div className="mb-2 flex items-center justify-between text-xs">
          <span className="text-muted-foreground">{t("filters.confidence.label")}</span>
          <span className="tabular-nums">
            {Math.round(value[0] * 100)}% – {Math.round(value[1] * 100)}%
          </span>
        </div>
        <Slider
          value={value}
          min={0}
          max={1}
          step={0.01}
          aria-label={t("filters.confidence.sliderLabel")}
          // Radix keeps the thumbs ordered, and the builder re-orders defensively
          // anyway — an inverted range would silently match nothing.
          onValueChange={([nextMin, nextMax]) => onChange(nextMin, nextMax)}
        />
        <button
          type="button"
          disabled={!active}
          onClick={() => onChange(null, null)}
          className="mt-2 w-full rounded-md border border-border px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted disabled:opacity-40"
        >
          {t("filters.confidence.clear")}
        </button>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/** Newest / oldest first. Maps to `-timestamp` / `timestamp`. */
export const OrderingToggle = ({
  value,
  onChange,
}: {
  value: AlertOrdering;
  onChange: (ordering: AlertOrdering) => void;
}) => {
  const { t } = useTranslation("alerts");
  return (
    <span className="flex items-center rounded-md border border-border p-0.5 text-xs">
      {(
        [
          { id: "-timestamp", labelKey: "filters.ordering.newest" },
          { id: "timestamp", labelKey: "filters.ordering.oldest" },
        ] as const
      ).map((option) => (
        <button
          key={option.id}
          type="button"
          onClick={() => onChange(option.id)}
          className={cn(
            "rounded px-2 py-1 transition-colors",
            value === option.id
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {t(option.labelKey)}
        </button>
      ))}
    </span>
  );
};
